/**
 * `#intelSelected` — the Key indicators sub-tab, rebuilt for the launch spec (client §5):
 * "priority class + the five constraints as tick/cross rows with values".
 *
 * So the chart is gone from this panel. Five bars of five different metrics in five
 * different units were never comparable side by side, and the client's own wording for a
 * row ("Rainfall 2025: 412 mm — lowest third for Burkina Faso ✓ flagged") carries the
 * value, the threshold and the verdict in one line. Two rows carry one extra line each:
 * productivity's GAEZ evidence (shown, never scored) and climate's 2025 anomaly.
 *
 * A country (`admin0`) row keeps the same five rows — each reading the share of that
 * country's watersheds flagged — plus the roll-up tiles.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import type { IntelUnitDetail } from '../../lib/api';
import { fmtCompactNum, fmtNum } from '../../lib/format';
import { StackedBar } from './charts/StackedBar';
import { StatTile, StatTiles } from './charts/StatTile';
import { CONSTRAINT_COLORS, CROP_COLORS } from './uc1Constants';
import { buildUc1Rows, type Uc1Values } from './uc1Rows';

export function SelectedArea({ detail }: { detail: IntelUnitDetail }) {
  const { t } = useTranslation();
  const values = detail.values as Uc1Values;
  const isCountry = detail.unit.level === 'admin0';
  const rows = buildUc1Rows({
    t: (key, options) => t(key, options ?? {}),
    drivers: detail.drivers,
    values,
    country: detail.unit.country,
    level: detail.unit.level,
  });
  const rollup = values.rollup;

  // The client's §5 title carries the verdict count, so a reader knows how many of the
  // five constraints the scorecard below is about before reading five rows. The second
  // clause only exists when something could not be assessed at all.
  const flagged = rows.filter((row) => row.status === 'flagged').length;
  const notAssessed = rows.filter((row) => row.status === 'not_assessed').length;
  const countLine = notAssessed
    ? t('intel.uc1.title.flagsNotAssessed', { n: flagged, m: notAssessed })
    : t('intel.uc1.title.flags', { n: flagged });

  return (
    <div id="intelSelected" className="intel-selected result-box">
      <h5>
        {detail.unit.name} — {detail.category_label} — {countLine}
      </h5>
      <div className="result-pad">
        <ul className="uc1-scorecard">
          {rows.map((row) => (
            <li
              key={row.dimension}
              data-testid={`uc1-row-${row.dimension}`}
              className={`uc1-row ${row.status}`}
              // One colour family per constraint, shared by the chip, the map and this
              // row's own tick and verdict word (spec §3 / acceptance §2).
              style={
                {
                  '--uc1-row-color': CONSTRAINT_COLORS[row.dimension],
                } as CSSProperties
              }
            >
              <span className="uc1-row-icon" aria-hidden="true">
                {row.icon}
              </span>
              <span className="uc1-row-text">
                {row.text}{' '}
                {/* The verdict is a word on screen, not only the glyph: the client's §5
                    row reads "… ✓ flagged", and a tick alone is not a reading. A row that
                    carries a `tail` puts the word *inside* its sentence instead of after
                    it — "Productivity: not assessed — no survey value", the status word
                    printed once rather than twice. */}
                <span className="uc1-row-status">{row.iconLabel}</span>
                {row.tail ? <>{' '}{row.tail}</> : null}
                {row.evidence ? (
                  <span
                    className="uc1-row-sub"
                    data-testid={`uc1-evidence-${row.dimension}`}
                  >
                    {row.evidence}
                  </span>
                ) : null}
                {row.subLine ? <span className="uc1-row-sub">{row.subLine}</span> : null}
              </span>
            </li>
          ))}
        </ul>

        {values.main_crops?.length ? (
          <StackedBar
            ariaLabel={t('intel.uc1.mainCropsLine')}
            unit="%"
            segments={values.main_crops.map((c, i) => ({
              id: c.crop,
              label: c.crop,
              value: c.share * 100,
              color: CROP_COLORS[i % CROP_COLORS.length]!,
            }))}
          />
        ) : null}

        {isCountry && rollup ? (
          <div className="intel-rollup" id="intelRollup" data-testid="intel-rollup">
            <StatTiles>
              {typeof rollup.n_watersheds === 'number' ? (
                <StatTile
                  id="uc1-rollup-watersheds"
                  value={fmtNum(rollup.n_watersheds, 0)}
                  label={t('intel.uc1.tile.watersheds')}
                />
              ) : null}
              {typeof rollup.ag_ha_flagged === 'number' ? (
                <StatTile
                  id="uc1-rollup-ag-area"
                  value={fmtCompactNum(rollup.ag_ha_flagged)}
                  unit="ha"
                  label={t('intel.uc1.tile.agAreaHigh')}
                />
              ) : null}
            </StatTiles>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default SelectedArea;
